const TRANSLATION_DEBUG_STORAGE_KEY = 'fu-devtools-translations'

// 'auto' lets hostname decide: production (faceup.com) → released snapshot,
// everywhere else (local/dev/preview) → latest unreleased copy. The explicit
// values let a developer pin a choice regardless of where the app is running.
type CopySource = 'auto' | 'released' | 'unreleased'

type TranslationDebugSettings = {
  showKeys: boolean
  showFocalazyLinks: boolean
  checkVariables: boolean
  copySource: CopySource
  // When true, Alt/Option-clicking a `<FormattedMessage>` opens an inline
  // popup wired up to Focalazy's dev API for editing the source-language
  // value. Independent of the iframe edit handshake — it lets devs translate
  // while running an app standalone, without opening Focalazy in another tab.
  inlineEdit: boolean
}

const defaultSettings: TranslationDebugSettings = {
  showKeys: false,
  showFocalazyLinks: false,
  checkVariables: false,
  copySource: 'auto',
  inlineEdit: false,
}

const loadSettings = (): TranslationDebugSettings => {
  try {
    const stored = window.localStorage.getItem(TRANSLATION_DEBUG_STORAGE_KEY)
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) }
    }
  } catch {
    /* localStorage may not be available */
  }
  return defaultSettings
}

// Module-level state — loaded once on import, before React render
const settings: TranslationDebugSettings = loadSettings()

const getTranslationDebugSettings = (): TranslationDebugSettings => settings

// Centralized resolver so all fetch paths agree on which snapshot to pull.
// Production is detected by hostname (faceup.com) because env flags aren't
// reliably available in every app that ships this package.
const isProductionHost = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  return window.location.href.includes('faceup.com')
}

const shouldFetchUnreleasedCopy = (): boolean => {
  switch (settings.copySource) {
    case 'released':
      return false
    case 'unreleased':
      return true
    default:
      return !isProductionHost()
  }
}

const FOCALAZY_BASE_URL = 'https://translations.faceup.com'
const FOCALAZY_PROJECT = 'monorepo'
const FOCALAZY_EDIT_URL = `${FOCALAZY_BASE_URL}/projects/${FOCALAZY_PROJECT}/translations?key=`

// Event dispatched by FMessage when the user Alt/Option-clicks a translation
// while `inlineEdit` is on. The devtools popup listens for it and opens the
// editor anchored to the originating element.
const INLINE_EDIT_EVENT = 'fu-tools:inline-translate'

type InlineEditEventDetail = {
  id: string
  anchor: { x: number; y: number; width: number; height: number }
}

export type { CopySource, InlineEditEventDetail, TranslationDebugSettings }
export {
  FOCALAZY_BASE_URL,
  FOCALAZY_EDIT_URL,
  FOCALAZY_PROJECT,
  getTranslationDebugSettings,
  INLINE_EDIT_EVENT,
  shouldFetchUnreleasedCopy,
  TRANSLATION_DEBUG_STORAGE_KEY,
}
